import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import cn from 'classnames';

import useWindowDimensions from 'hooks/useWindowDimensions';

import * as routes from 'pages/routes';

import logo from 'public/images/logo.png';

import * as companyResource from 'resources/company/hooks';
import * as teamsResources from 'resources/teams/hooks';
import * as userResources from 'resources/user/hooks';

import isInIframe from 'utils/isInIframe';

import useUserTabs from '../hooks/useUserTabs';

import SideBar from '../SideBar';
import DesktopSideBar from '../DesktopSideBar';

import LearnMoreWidget from './LearnMoreWidget';

import styles from './styles.module.scss';

const MarketingLayout = ({ children, pageContentOffset }) => {
  const router = useRouter();
  const { isDesktopWidth } = useWindowDimensions();
  const isIframe = isInIframe();

  const { companyLogoUrl, companyBackground } = companyResource.useCompanyBranding();
  const { data: user } = userResources.useCurrentUser();
  const { data: team } = teamsResources.useGetTeamById(user?.team_id, { enabled: !!user?.team_id });

  const brandingLogoUrl = useMemo(() => {
    // If user is a realtor or client, use the team logo
    if (user?.type === 'realtor' || user?.type === 'client') {
      return team?.logo_url || companyLogoUrl;
    }

    return companyLogoUrl;
  }, [team, companyLogoUrl, user]);

  const [isSideBarVisible, setIsSideBarVisible] = useState(false);
  const [isLearnMoreVisible, setIsLearnMoreVisible] = useState(true);

  const onCloseSideBar = useCallback(() => {
    setIsSideBarVisible(false);
  }, []);

  const onSideBarToggle = useCallback(() => {
    setIsSideBarVisible(!isSideBarVisible);
  }, [isSideBarVisible]);

  const handleCloseLearnMore = useCallback(() => {
    setIsLearnMoreVisible(false);
  }, []);

  const { menuTabs } = useUserTabs({ user: null, company: null });

  const onTabClick = useCallback((tab) => {
    if (tab.name === routes.tabs.bottomTabs.MORE) {
      onSideBarToggle();
    }
  }, [onSideBarToggle]);

  const isBottomNavBarVisible = !isDesktopWidth
    && routes.configurations().find((item) => item.path === router.route && item.tab);

  return (
    <div className={cn({
      [styles.mainLayoutWrapper]: true,
      [styles.isIframe]: isIframe,
    })}
    >
      <header
        className={cn({
          [styles.mainLayoutHeader]: true,
          [styles.withBranding]: brandingLogoUrl,
        })}
        style={{ backgroundColor: companyBackground }}
      >
        <img
          src={brandingLogoUrl || logo}
          className={cn({
            [styles.logo]: true,
            [styles.withBranding]: brandingLogoUrl,
          })}
          alt="logo"
        />
      </header>

      <main className={cn({
        [styles.main]: true,
        [styles[pageContentOffset]]: pageContentOffset,
      })}
      >
        {children}

        {isDesktopWidth && (
          <LearnMoreWidget
            isOpen={isLearnMoreVisible}
            onClose={handleCloseLearnMore}
            className={styles.widget}
          />
        )}
      </main>

      {!isIframe && (
        <>
          {isBottomNavBarVisible && (
            <nav className={styles.navigation}>
              {menuTabs.map((item) => {
                const isActiveTab = item.paths.includes(router.route);

                return (
                  <div
                    key={item.name}
                    className={cn({
                      [styles.tab]: true,
                      [styles.disabledTab]: item.name !== routes.tabs.bottomTabs.MORE,
                      [styles.activeTab]: isActiveTab,
                    })}
                    style={{ width: `${100 / menuTabs.length}%` }}
                    aria-hidden
                    onClick={() => onTabClick(item)}
                  >
                    {item.icon()}
                    <p>{item.name}</p>
                  </div>
                );
              })}
            </nav>
          )}

          {isDesktopWidth
            ? (
              <DesktopSideBar
                disabled
              />
            )
            : (
              <SideBar
                isShown={isSideBarVisible}
                onClose={onCloseSideBar}
                disabled
              />
            )}
        </>
      )}
    </div>
  );
};

MarketingLayout.propTypes = {
  children: PropTypes.node.isRequired,
  pageContentOffset: PropTypes.string,
};

MarketingLayout.defaultProps = {
  pageContentOffset: 'defaultOffset',
};

export default MarketingLayout;
